import { useCallback, useEffect, useRef } from 'react';
import { buildCallTipText, parseTipJson, tipMetaUsdForCreator } from '../../../../../shared/utils/chatUtil';
import { JUICYHOUSE_CALL_EVENT } from '../../../../../shared/utils/matrix/matrixRoomUtils';

const CALL_TIP_TYPES = new Set(['tip_call', 'call_tip']);

/**
 * Creator in-call tip toast + session tip USD ledger.
 * Only counts Matrix `call_tip` events (and legacy chat tips with tip_type tip_call).
 * Does not use message-tips earnings totals — those are lifetime chat tips, not this call.
 */
export const useCallTipEarnings = ({
  callSessionTipUsdRef,
  client,
  handleTipSuccessInCallBase,
  isCreator,
  isInitialized,
  opponentDisplayName,
  roomId,
  setTipText,
  bumpCallTipLedger,
}) => {
  const seenTipEventIdsRef = useRef(new Set());

  const extractTipMetaFromPayload = useCallback((payload) => {
    if (!payload) return null;

    if (typeof payload === 'string') {
      return parseTipJson(payload);
    }

    if (typeof payload === 'object') {
      if (
        typeof payload.amount !== 'undefined' ||
        typeof payload.baseAmount !== 'undefined' ||
        typeof payload.realPrice !== 'undefined' ||
        typeof payload.tip_type !== 'undefined'
      ) {
        return payload;
      }
    }

    return null;
  }, []);

  const isCallScopedTipMeta = useCallback((tipMeta) => {
    if (!tipMeta || typeof tipMeta !== 'object') return false;
    const tipType = String(tipMeta.tip_type || '').toLowerCase();
    return CALL_TIP_TYPES.has(tipType);
  }, []);

  const handleTipSuccessInCall = useCallback(
    (tipMessage, tipMeta) => {
      if (tipMeta && typeof tipMeta === 'object') {
        setTipText(buildCallTipText({ tipMeta, isCreator, opponentDisplayName }));
      }

      handleTipSuccessInCallBase(tipMessage, tipMeta);
    },
    [
      handleTipSuccessInCallBase,
      isCreator,
      opponentDisplayName,
      setTipText,
    ]
  );

  useEffect(() => {
    seenTipEventIdsRef.current = new Set();
  }, [roomId]);

  useEffect(() => {
    if (!client || !isInitialized || !roomId) return undefined;

    const extractTipMetaFromEvent = (event) => {
      const eventType = event.getType?.();
      const content = event.getContent?.();

      // In-call tips: custom call event (kept out of chat timeline bubbles).
      if (eventType === JUICYHOUSE_CALL_EVENT) {
        if (content?.type !== 'call_tip') return null;
        return extractTipMetaFromPayload(content?.body);
      }

      // Legacy: older clients sent call tips as m.room.message JSON with tip_type tip_call.
      // Do not treat ordinary chat tips or any "$…" text line as call earnings.
      if (eventType === 'm.room.message') {
        if (content?.msgtype !== 'm.text' || typeof content?.body !== 'string') return null;
        const parsed = extractTipMetaFromPayload(content.body);
        if (!isCallScopedTipMeta(parsed)) return null;
        return parsed;
      }

      return null;
    };

    const onTimeline = (event, room, toStartOfTimeline) => {
      if (toStartOfTimeline) return;
      if (!event || room?.roomId !== roomId) return;
      const parsedTipMeta = extractTipMetaFromEvent(event);
      if (!parsedTipMeta) return;

      const eventId = event.getId?.() || null;
      if (eventId) {
        if (seenTipEventIdsRef.current.has(eventId)) return;
        seenTipEventIdsRef.current.add(eventId);
      }

      if (isCreator && callSessionTipUsdRef) {
        const add = tipMetaUsdForCreator(parsedTipMeta);
        if (add > 0) {
          callSessionTipUsdRef.current = Number(callSessionTipUsdRef.current || 0) + add;
          bumpCallTipLedger?.();
        }
      }

      setTipText(buildCallTipText({ tipMeta: parsedTipMeta, isCreator, opponentDisplayName }));
    };

    client.on?.('Room.timeline', onTimeline);
    return () => {
      client.removeListener?.('Room.timeline', onTimeline);
    };
  }, [
    client,
    extractTipMetaFromPayload,
    isCallScopedTipMeta,
    isCreator,
    isInitialized,
    callSessionTipUsdRef,
    opponentDisplayName,
    roomId,
    setTipText,
    bumpCallTipLedger,
  ]);

  return { handleTipSuccessInCall };
};
